/**
 * Tenant Context Dropdown
 *
 * A dropdown component for quick tenant context switching (super admin only).
 * Replaces the need to navigate to TenantsPage for each switch.
 *
 * Part of: AI Platform Phase 3.2.6 - Access Control UI Completion
 * Task: 3.2.6.5
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useAdminContext } from '../../contexts/AdminContextProvider';
import { GlobeIcon, ChevronDownIcon, CheckIcon } from './Icons';

// =============================================================================
// Types
// =============================================================================

interface Tenant {
  id: string;
  name: string;
  slug: string;
  status: string;
  branding?: {
    primaryColor?: string;
  };
}

interface TenantContextDropdownProps {
  className?: string;
}

// =============================================================================
// Component
// =============================================================================

const TenantContextDropdown: React.FC<TenantContextDropdownProps> = ({ className }) => {
  const { authFetch } = useAuth();
  const { activeTenant, isInTenantContext, enterTenant, exitTenant, canSwitchContext } = useAdminContext();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Don't render for non-super-admins
  if (!canSwitchContext) {
    return null;
  }

  // Fetch tenants when dropdown opens (only once per session)
  useEffect(() => {
    if (isOpen && !hasFetched) {
      fetchTenants();
    }
  }, [isOpen, hasFetched]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchTenants = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await authFetch('/api/admin/tenants');
      if (response.ok) {
        const data = await response.json();
        // Filter to only active tenants
        const activeTenants = (data.tenants || data || []).filter(
          (t: Tenant) => t.status === 'active'
        );
        setTenants(activeTenants);
        setHasFetched(true);
      }
    } catch (err) {
      console.error('Failed to fetch tenants:', err);
    }
    setIsLoading(false);
  }, [authFetch]);

  const handleSelectPlatform = () => {
    if (isInTenantContext) {
      exitTenant();
    }
    setIsOpen(false);
  };

  const handleSelectTenant = (tenant: Tenant) => {
    enterTenant({ id: tenant.id, name: tenant.name, slug: tenant.slug, branding: tenant.branding });
    setIsOpen(false);
  };

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Current display
  const currentLabel = isInTenantContext && activeTenant
    ? activeTenant.name
    : 'Platform (All Tenants)';
  const currentColor = isInTenantContext && activeTenant?.branding?.primaryColor
    ? activeTenant.branding.primaryColor
    : null;

  return (
    <div
      ref={dropdownRef}
      className={`tenant-context-dropdown ${className || ''}`}
      style={{ position: 'relative', display: 'inline-block' }}
    >
      {/* Trigger Button */}
      <button
        className="btn btn-ghost"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label="Switch tenant context"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '6px 12px',
          border: '1px solid var(--border)',
          borderRadius: '6px',
          background: 'var(--bg-secondary)',
          minWidth: '180px',
          justifyContent: 'space-between',
          cursor: 'pointer',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {currentColor ? (
            <span
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: currentColor,
                flexShrink: 0,
              }}
            />
          ) : (
            <GlobeIcon size={14} />
          )}
          <span style={{ fontSize: '13px', fontWeight: 500 }}>{currentLabel}</span>
        </span>
        <ChevronDownIcon
          size={14}
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          role="listbox"
          aria-label="Tenant selection"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: '4px',
            minWidth: '220px',
            background: 'var(--bg-primary)',
            border: '1px solid var(--border)',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            overflow: 'hidden',
          }}
        >
          {/* Platform Option */}
          <button
            role="option"
            aria-selected={!isInTenantContext}
            onClick={handleSelectPlatform}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
              padding: '10px 12px',
              border: 'none',
              background: !isInTenantContext ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
              cursor: 'pointer',
              fontSize: '13px',
              textAlign: 'left',
              color: 'inherit',
            }}
            onMouseEnter={(e) => {
              if (isInTenantContext) {
                e.currentTarget.style.background = 'var(--bg-secondary)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = !isInTenantContext ? 'rgba(59, 130, 246, 0.1)' : 'transparent';
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <GlobeIcon size={14} />
              <span>Platform (All Tenants)</span>
            </span>
            {!isInTenantContext && <CheckIcon size={14} style={{ color: 'var(--primary)' }} />}
          </button>

          {/* Divider */}
          <div style={{ height: '1px', background: 'var(--border)', margin: '4px 0' }} />

          {/* Tenants List */}
          {isLoading ? (
            <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px' }}>
              Loading tenants...
            </div>
          ) : tenants.length === 0 ? (
            <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px' }}>
              No active tenants
            </div>
          ) : (
            <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
              {tenants.map((tenant) => {
                const isSelected = isInTenantContext && activeTenant?.id === tenant.id;
                const tenantColor = tenant.branding?.primaryColor || '#6b7280';
                return (
                  <button
                    key={tenant.id}
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleSelectTenant(tenant)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '8px',
                      padding: '10px 12px',
                      border: 'none',
                      background: isSelected ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                      cursor: 'pointer',
                      fontSize: '13px',
                      textAlign: 'left',
                      color: 'inherit',
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.background = 'var(--bg-secondary)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = isSelected ? 'rgba(59, 130, 246, 0.1)' : 'transparent';
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span
                        style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          backgroundColor: tenantColor,
                          flexShrink: 0,
                        }}
                      />
                      <span>{tenant.name}</span>
                    </span>
                    {isSelected && <CheckIcon size={14} style={{ color: 'var(--primary)' }} />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TenantContextDropdown;
